import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react'

import { cn } from '@/lib/cn'

/** Every text-entry control shares this ground, border and focus behaviour. */
const CONTROL_BASE =
  'w-full rounded-md border bg-surface-raised text-ink shadow-xs ' +
  'transition-[border-color,box-shadow] duration-100 ease-out ' +
  'placeholder:text-ink-muted ' +
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-muted'

const CONTROL_BORDER = 'border-line hover:border-line-strong'
const CONTROL_INVALID = 'border-critical hover:border-critical'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leading?: ReactNode
  trailing?: ReactNode
  invalid?: boolean
  /** `sm` for toolbars and table controls; `md` everywhere else. */
  inputSize?: 'sm' | 'md'
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leading, trailing, invalid, inputSize = 'md', className, ...props },
  ref,
) {
  return (
    <div className="relative flex w-full items-center">
      {leading ? (
        <span
          className="pointer-events-none absolute left-2.5 flex text-ink-muted"
          aria-hidden="true"
        >
          {leading}
        </span>
      ) : null}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          CONTROL_BASE,
          invalid ? CONTROL_INVALID : CONTROL_BORDER,
          inputSize === 'sm' ? 'h-8 text-sm' : 'h-9 text-base',
          leading ? 'pl-8.5' : 'pl-2.5',
          trailing ? 'pr-8.5' : 'pr-2.5',
          className,
        )}
        {...props}
      />
      {trailing ? (
        <span className="absolute right-2 flex text-ink-muted">{trailing}</span>
      ) : null}
    </div>
  )
})

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ invalid, className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        CONTROL_BASE,
        invalid ? CONTROL_INVALID : CONTROL_BORDER,
        'scrollbar-slim p-2.5 text-sm',
        className,
      )}
      {...props}
    />
  )
})

export interface FieldProps {
  label: string
  hint?: ReactNode
  /** Shown in place of the hint, and marks the control invalid. */
  error?: string
  required?: boolean
  htmlFor?: string
  children: ReactNode
  className?: string
}

/**
 * Label, control and helper text, laid out the same way every time.
 *
 * The label is a real `<label for>`: a paragraph styled to look like one leaves
 * the control unnamed for a screen reader and un-clickable for everyone.
 */
export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
  className,
}: FieldProps) {
  const generated = useId()
  const describedBy = `${htmlFor ?? generated}-hint`

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className="flex items-center gap-1 text-2xs font-semibold tracking-[0.06em] text-ink-muted uppercase"
      >
        {label}
        {required ? (
          <span className="text-critical" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <p id={describedBy} className="text-xs text-critical" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={describedBy} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
